import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 30_000;

/**
 * Polls /api/inbox/unread/count (or the admin variant) every 30s and returns
 * the current unread count. Also refetches whenever the user navigates so the
 * badge clears immediately after they open the inbox.
 */
export default function useInboxUnreadCount() {
  const { user } = useAuth();
  const location = useLocation();
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const isAdminSide =
    user?.role === "admin" ||
    (user?.role === "sub_admin" && (user?.admin_permissions || []).includes("inbox"));

  useEffect(() => {
    if (!user) { setCount(0); return; }
    const url = isAdminSide ? "/admin/inbox/unread/count" : "/inbox/unread/count";
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await api.get(url);
        if (!cancelled) setCount(Number(data?.unread || 0));
      } catch (e) {
        // Non-blocking — a network hiccup shouldn't fill logs with warnings.
        if (!cancelled) setCount((prev) => prev); // keep previous
      }
    };

    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(timerRef.current); };
    // Re-fetch on route change so the badge clears the moment a user views the inbox.
  }, [user?.id, isAdminSide, location.pathname]);

  return count;
}
